import { useUser } from "@/hooks/AppContext/user.hook";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Check, ChevronsUpDown, User } from "lucide-react";
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

type AssigneePickerProps = {
  assignedTo: number | null;
  modal: boolean;
  handleFieldAssignedTo: (id: number) => void;
  setAssignedTo?: Dispatch<SetStateAction<number | null>>;
  isOpen?: React.MutableRefObject<boolean>;
  disabled?: boolean;
};

const AssigneePicker: React.FC<AssigneePickerProps> = ({
  assignedTo,
  modal,
  handleFieldAssignedTo,
  setAssignedTo,
  isOpen,
  disabled,
}) => {
  const { getUserFullNameByID, getUserProfileImageByID } = useUser();
  const { allAppUsers } = useContext(AppContext);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleAssignedToChange = (id: number) => {
    if (setAssignedTo) {
      setAssignedTo(id);
    }
    handleFieldAssignedTo(id);
  };

  useEffect(() => {
    if (isOpen) {
      isOpen.current = isPopoverOpen;
    }
  }, [isPopoverOpen, isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (disabled) {
      return;
    }
    setIsPopoverOpen(open);
  };

  return (
    <Popover modal={modal} open={isPopoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className="w-full">
        <div
          id="edit-assignee"
          className="ps-0 flex flex-row items-center justify-between w-full text-left"
        >
          <Button
            className="truncate w-full justify-between"
            variant="outline"
            role="combobox"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {assignedTo ? (
              <div className="flex items-center gap-2">
                {getUserProfileImageByID(assignedTo) ? (
                  <img
                    className="size-4 rounded-full bg-primary shrink-0"
                    src={getUserProfileImageByID(assignedTo)}
                    alt={getUserFullNameByID(assignedTo)}
                  />
                ) : (
                  <User className="size-4 rounded-full bg-primary shrink-0" />
                )}
                <div>{getUserFullNameByID(assignedTo)}</div>
              </div>
            ) : (
              "Select assignee"
            )}
            <ChevronsUpDown className="ml-3 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder="Search assignee..." />
          <CommandList>
            <ScrollArea className="h-[200px]">
              <CommandEmpty>No assignees found.</CommandEmpty>
              <CommandGroup>
                {allAppUsers.map((user) => (
                  <div key={user.id} onMouseDown={(e) => e.stopPropagation()}>
                    <CommandItem
                      onSelect={() => {
                        handleAssignedToChange(user.id);
                        setIsPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          assignedTo === user.id ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <div className="flex items-center gap-2">
                        {user.profile_picture ? (
                          <img
                            className="size-4 rounded-full bg-primary shrink-0"
                            src={user.profile_picture}
                            alt={`${user.first_name} ${user.surname}`}
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}
                        <div>
                          {user.first_name} {user.surname}
                        </div>
                      </div>
                    </CommandItem>
                  </div>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AssigneePicker;
